/**
 * PATH: packages/email-hub/src/feature/components/ExternalLinkChip.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENT-EXTERNALLINKCHIP-001
 * PURPOSE: Render one EmailExternalLink as a chip — the visual
 *          peer of AttachmentChip, but for share URLs that were
 *          DETECTED in the email body and auto-downloaded into
 *          QDrive in the background (rather than directly attached
 *          to the Graph message). Email→Quote Orchestration ARC
 *          § Phase 5 / Scope 2A.
 *
 * INPUT:   One EmailExternalLink row from the email-detail GET.
 *
 * STATES:  - pending     : "Queued to download"
 *          - downloading : %-progress bar + spinner badge
 *                          (vanishes upon completion, matching the
 *                          operator's 2026-05-19 brief)
 *          - done        : provider badge + QDrive ref + actions
 *                          (open in QDrive, open original)
 *          - failed      : red banner with the `last_error`
 *          - skipped     : muted "open original link" affordance
 *                          (provider not yet supported, or file
 *                          too large for inline ingest)
 *
 * NOTES:   This is a sibling of AttachmentChip rather than a
 *          discriminated-union extension because the underlying
 *          model (`EmailExternalLink`) has a materially different
 *          shape (no `download_url`, no `mime_type` until ingest
 *          lands, no `content_id`, plus the per-link
 *          `download_status` / `progress_pct` lifecycle that
 *          regular attachments never have). One chip per provider-
 *          flavoured shape keeps the read-the-source story simple.
 */

import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  AlertCircle,
  Cloud,
  ExternalLink,
  FileBox,
  FolderOpen,
  Link2,
  Loader2,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { cn } from '../../_internal/lib/utils'
import { useToast } from '../../_internal/components/Toast'
import { formatFileSize } from '../lib/utils'
import type { EmailExternalLink } from '../hooks/useEmails'

interface ExternalLinkChipProps {
  link: EmailExternalLink
  /** Email id — used to build the QDrive deep-link with a
   *  ``ref=email_external_link&email_id=<eid>`` breadcrumb so a
   *  QDrive page opened from a chip can render a "back to email"
   *  affordance in a future MR. */
  emailId: string
}

// ---------------------------------------------------------------------------
// Provider visual mapping. Keep the palette deliberately distinct
// from AttachmentChip (which uses kind-of-file palettes) — these
// are PROVIDER-of-link palettes, mirroring the brand colour the
// operator already associates with each (blue=OneDrive,
// teal=SharePoint, yellow=Google Drive, slate=other/unknown).
// ---------------------------------------------------------------------------

interface ProviderStyle {
  label: string
  icon: LucideIcon
  tintClass: string
  iconClass: string
  badgeClass: string
}

const PROVIDER_STYLE: Record<EmailExternalLink['provider'], ProviderStyle> = {
  onedrive: {
    label: 'OneDrive',
    icon: Cloud,
    tintClass: 'bg-blue-500/10',
    iconClass: 'text-blue-600 dark:text-blue-400',
    badgeClass: 'text-blue-700 dark:text-blue-300',
  },
  sharepoint: {
    label: 'SharePoint',
    icon: FolderOpen,
    tintClass: 'bg-teal-500/10',
    iconClass: 'text-teal-600 dark:text-teal-400',
    badgeClass: 'text-teal-700 dark:text-teal-300',
  },
  google_drive: {
    label: 'Google Drive',
    icon: FileBox,
    tintClass: 'bg-amber-500/10',
    iconClass: 'text-amber-600 dark:text-amber-400',
    badgeClass: 'text-amber-700 dark:text-amber-300',
  },
  other: {
    label: 'External link',
    icon: ExternalLink,
    tintClass: 'bg-slate-500/10',
    iconClass: 'text-slate-600 dark:text-slate-300',
    badgeClass: 'text-slate-700 dark:text-slate-300',
  },
}

const STATUS_LABEL: Record<EmailExternalLink['download_status'], string> = {
  pending: 'Queued to download',
  downloading: 'Downloading…',
  done: 'In QDrive',
  failed: 'Download failed',
  skipped: 'Download not supported',
}

export default function ExternalLinkChip({ link, emailId }: ExternalLinkChipProps) {
  const { toast } = useToast()
  const navigate = useNavigate()
  const style = PROVIDER_STYLE[link.provider] ?? PROVIDER_STYLE.other
  const Icon = style.icon

  // Operator-visible filename: prefer the upstream-resolved name
  // (from the Graph /driveItem metadata fetch), fall back to a
  // sensible "OneDrive shared file" placeholder so we never render
  // an empty chip title.
  const title = link.resolved_filename || `${style.label} shared file`

  // Progress-bar visibility — mirrors AttachmentChip's "vanishes
  // upon completion" semantics: only show when status is
  // `downloading` AND progress is strictly between 0 and 100. A 0
  // OR a 100 collapses to no bar so the visual stays clean.
  const isDownloading = link.download_status === 'downloading'
  const showProgress =
    isDownloading && link.progress_pct > 0 && link.progress_pct < 100
  const clampedProgress = Math.max(0, Math.min(100, link.progress_pct))

  // QDrive deep-link parity with AttachmentChip / AttachmentList
  // (P4 / Scope 1C). Only enabled once download_status === 'done'
  // AND the FileRecord landed (`qdrive_file_id`).
  const buildQDriveUrl = useCallback((): string | null => {
    if (!link.qdrive_file_id) return null
    const params = new URLSearchParams()
    params.set('file_id', link.qdrive_file_id)
    params.set('ref', 'email_external_link')
    params.set('email_id', emailId)
    return `/qdrive?${params.toString()}`
  }, [link.qdrive_file_id, emailId])

  const openInQDrive = useCallback(() => {
    const url = buildQDriveUrl()
    if (!url) {
      toast(`This link hasn't been downloaded into QDrive yet`, 'info')
      return
    }
    navigate(url)
  }, [buildQDriveUrl, navigate, toast])

  const copyQDriveLink = useCallback(async () => {
    const url = buildQDriveUrl()
    if (!url) {
      toast(`This link hasn't been downloaded into QDrive yet`, 'info')
      return
    }
    const absolute =
      typeof window !== 'undefined' && window.location?.origin
        ? `${window.location.origin}${url}`
        : url
    try {
      await navigator.clipboard.writeText(absolute)
      toast(`Copied QDrive link`, 'success')
    } catch {
      toast(`Could not copy QDrive link`, 'error')
    }
  }, [buildQDriveUrl, toast])

  const openOriginalUrl = useCallback(() => {
    if (typeof window === 'undefined') return
    window.open(link.detected_url, '_blank', 'noopener,noreferrer')
  }, [link.detected_url])

  // The chip is clickable when we can route the operator to
  // QDrive; otherwise the click opens the original share URL in a
  // new tab (the same fallback the chip-less email body used
  // before P5 shipped).
  const primaryAction = link.qdrive_file_id ? openInQDrive : openOriginalUrl

  return (
    <div
      className={cn(
        'group relative flex items-center gap-3 p-2.5 pr-2',
        'bg-background rounded-lg border border-border w-full text-left',
        'transition-all duration-150',
      )}
      data-testid="external-link-chip"
      data-provider={link.provider}
      data-status={link.download_status}
    >
      {/* Icon slot — provider-coloured tinted square. */}
      <div
        className={cn(
          'h-10 w-10 rounded-lg flex items-center justify-center shrink-0',
          style.tintClass,
        )}
      >
        <Icon className={cn('h-5 w-5', style.iconClass)} />
      </div>
      {isDownloading && (
        <span
          aria-hidden
          className="absolute top-1.5 left-1.5 h-3.5 w-3.5 rounded-full bg-primary/15 flex items-center justify-center"
          data-testid="external-link-chip-progress-badge"
        >
          <Loader2 className="h-2.5 w-2.5 animate-spin text-primary" />
        </span>
      )}

      {/* Title + meta. The main action is fire-on-click on the
          title block so the chip behaves like a button without
          becoming one (the inner action buttons would otherwise
          fire the wrapping <button>'s click as well). */}
      <button
        type="button"
        onClick={primaryAction}
        className="min-w-0 flex-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded"
        title={
          link.qdrive_file_id
            ? `Open ${title} in QDrive`
            : `Open original ${style.label} link in new tab`
        }
      >
        <p className="font-medium text-foreground text-sm truncate leading-tight">
          {title}
        </p>
        <p className="text-[11px] text-muted-foreground leading-tight mt-0.5 flex items-center gap-1.5 flex-wrap">
          <span className={cn('font-medium', style.badgeClass)}>{style.label}</span>
          <span aria-hidden className="opacity-50">·</span>
          <span>{STATUS_LABEL[link.download_status]}</span>
          {link.total_bytes && link.total_bytes > 0 && (
            <>
              <span aria-hidden className="opacity-50">·</span>
              <span>{formatFileSize(link.total_bytes)}</span>
            </>
          )}
          {link.qdrive_file_id && (
            <>
              <span aria-hidden className="opacity-50">·</span>
              <span
                className="inline-flex items-center gap-0.5 text-primary/80"
                title={
                  link.qdrive_ref_code
                    ? `In QDrive · ref ${link.qdrive_ref_code}`
                    : 'In QDrive'
                }
                data-testid="external-link-chip-qdrive-badge"
              >
                <Link2 className="h-3 w-3" aria-hidden />
                <span className="font-medium">QDrive</span>
              </span>
            </>
          )}
        </p>
        {link.download_status === 'failed' && link.last_error && (
          <p className="text-[11px] text-rose-600 dark:text-rose-400 mt-1 flex items-start gap-1">
            <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" aria-hidden />
            <span className="truncate" title={link.last_error}>{link.last_error}</span>
          </p>
        )}
      </button>

      {/* Action cluster — varies by status. */}
      <div className="flex items-center gap-0.5 shrink-0">
        {link.qdrive_file_id && (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); copyQDriveLink() }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); copyQDriveLink() }
            }}
            className={cn(
              'p-1.5 rounded text-muted-foreground',
              'hover:bg-primary/10 hover:text-primary transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
            )}
            title="Copy QDrive link"
          >
            <Link2 className="h-4 w-4" />
          </span>
        )}
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => { e.stopPropagation(); openOriginalUrl() }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openOriginalUrl() }
          }}
          className={cn(
            'p-1.5 rounded text-muted-foreground',
            'hover:bg-accent hover:text-foreground transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
          )}
          title="Open original share link in new tab"
        >
          <ExternalLink className="h-4 w-4" />
        </span>
      </div>

      {showProgress && (
        <span
          aria-hidden
          className="absolute left-0 right-0 bottom-0 h-0.5 bg-primary/10 overflow-hidden rounded-b-lg"
          data-testid="external-link-chip-progress-bar"
        >
          <span
            className="block h-full bg-primary transition-[width] duration-200 ease-out"
            style={{ width: `${clampedProgress}%` }}
            role="progressbar"
            aria-valuenow={Math.round(clampedProgress)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`Downloading ${title}`}
          />
        </span>
      )}
    </div>
  )
}
